import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, navigate } from "@gatsbyjs/reach-router";
import Fab from "@material-ui/core/Fab";
import CloseIcon from '@material-ui/icons/Close';
import { useLazyQuery, useQuery } from "@apollo/client";
import { wasAssign, wasDelivery, wasPickUp } from "../utils";
import { loader } from "graphql.macro";
import { errorMessage } from "../utils/messages";
import { okSound, duplicitySound, errorSound } from "../utils/sounds/";
import { useSnackbar } from "notistack";
import { makeStyles } from "@material-ui/core/styles";
import BarcodeScannerComponent from "../components/dynamsoftScanner/scanner";
import { isMine } from "./shipmentsHelper";
import Grid from "@material-ui/core/Grid";
import Switch from "@material-ui/core/Switch";
import Chip from '@material-ui/core/Chip';
import Paper from '@material-ui/core/Paper';
import MultiSelectPanel from "../components/shipments/MultiSelectPanel";
import { persistActualUrl } from "../utils/history/lastUrlPersist";
import { Loading } from "../components";

const GET_SHIPMENT_SCANNER = loader('../gql/query/shipmentScannerNote.graphql');
const GET_FILTER = loader('../gql/query/client/filter.graphql');

const useStyles = makeStyles(theme => ({
    code: {
        textAlign: 'center',
        fontSize: '20px',
        marginBottom: 20,
        marginLeft: 15,
    },
    radio: {
        margin: '5px 20px',
    },
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        listStyle: 'none',
        padding: theme.spacing(0.5),
        margin: 0,
    },
    chip: {
        margin: theme.spacing(0.5),
    },
    spaceForMultiPanel: {
        marginBottom: 153,
    },
    wrapper: {
        margin: "2vmin auto",
        marginTop: -80,
        textAlign: "center",
        fontSize: "medium",
        height: "54vh",
        width: "100vw",
        [theme.breakpoints.up('sm')]: {
            width: `calc(100vw - ${theme.custom?.main?.drawerWidth || 0}px)`,
        },
    },
}));

const Scanner = () => {
    const classes = useStyles();
    const [code, setCode] = useState(null);
    const [isMulti, setMulti] = useState(false);
    const [multiselectShipments, setMultiselectShipments] = useState([]);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { data: dataLocal } = useQuery(GET_FILTER);

    // Ref to hold the latest scan handler, ensuring the scanner uses the up-to-date version
    const handleScanRef = useRef(null);

    const normalizeITFCode = useCallback(scannedCode => {
        return scannedCode ? String(scannedCode).replace(/\b0+/g, '') : '';
    }, []);

    const modifyCode = useCallback(scannedCode => {
        // used for modify code and remove preffix like mall, dont need now
        return scannedCode;
    }, []);

    const [getShipment, { loading }] = useLazyQuery(GET_SHIPMENT_SCANNER, {
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            const shipmentResults = data?.shipmentNoteSearch;
            if (shipmentResults?.length === 1) {
                const shipment = shipmentResults[0];
                const userCode = data?.user?.kod;
                const wasPickedUp = wasPickUp(shipment);
                const wasDelivered = wasDelivery(shipment);
                const wasAssigned = wasAssign(shipment);
                const isMineVar = userCode ? isMine(shipment, userCode) : false;

                let url = `/scanner/${shipment.Zasielka_Kod}`;
                if (!wasDelivered && wasPickedUp && isMineVar) {
                    url = `/scanner/${shipment.Zasielka_Kod}/delivery`;
                } else if (!wasDelivered && !wasPickedUp && wasAssigned && isMineVar) {
                    url = `/scanner/${shipment.Zasielka_Kod}/pickup`;
                }
                setCode(null);
                navigate(url);
            } else if (shipmentResults?.length > 1) {
                const originalCode = code;
                setCode(null);
                if (originalCode) navigate(`scanner/search/note/${originalCode}/`);
            } else {
                errorMessage(`Zásielka: ${code} neexistuje alebo k nej nemáte oprávnenie.`, enqueueSnackbar, closeSnackbar);
            }
        },
        onError: (error) => {
            errorMessage(`Chyba pri vyhľadávaní: ${error.message}`, enqueueSnackbar, closeSnackbar);
        }
    });

    const handleDeleteFromMultiselect = useCallback((shipmentId) => {
        setMultiselectShipments((current) => {
            if (shipmentId instanceof RegExp) return current.filter((chip) => !shipmentId.test(chip));
            const toDelete = String(shipmentId);
            return current.filter((chip) => chip !== toDelete);
        });
    }, []);

    const handleDeleteChip = useCallback((chipToDelete) => () => {
        handleDeleteFromMultiselect(chipToDelete);
    }, [handleDeleteFromMultiselect]);

    const addToMulti = useCallback((id) => {
        setMultiselectShipments(current => {
            const stringId = String(id);
            if (!current.includes(stringId)) {
                okSound();
                return [...current, stringId];
            }
            duplicitySound();
            return current;
        });
    }, []);

    const processGo4Code = useCallback((newCode) => {
        const normalizedCode = normalizeITFCode(newCode);
        if (normalizedCode && normalizedCode.length > 0) {
            getShipment({ variables: { note: normalizedCode, fromDate: dataLocal?.fromDate, toDate: dataLocal?.toDate } });
        } else if (newCode) {
            errorMessage(`Neplatný kód po spracovaní: ${newCode}`, enqueueSnackbar, closeSnackbar);
            setCode(newCode);
        }
    }, [getShipment, dataLocal, normalizeITFCode, enqueueSnackbar, closeSnackbar, setCode]);

    const actualHandleScan = useCallback((data) => {
        if (data) {
            const stringData = String(data);
            if (stringData !== code) {
                setCode(stringData);
                const modifiedData = modifyCode(stringData);
                if (isMulti) {
                    addToMulti(modifiedData);
                } else {
                    okSound();
                    processGo4Code(modifiedData);
                }
            } else {
                duplicitySound();
            }
        }
    }, [ code, isMulti, setCode, modifyCode, addToMulti, processGo4Code ]);

    useEffect(() => {
        handleScanRef.current = actualHandleScan;
    }, [actualHandleScan]);

    // A stable function wrapper (empty dependency array) to pass to the scanner component.
    // This wrapper always calls the *latest* handler stored in the ref.
    const stableScanHandlerWrapper = useCallback((data) => {
        handleScanRef.current?.(data); // Call the function in the ref if it exists
    }, []); // No dependencies -> Stable reference

    const handleResetMultiSelect = useCallback(() => setMultiselectShipments([]), []);

    const handleSwitchMulti = useCallback(() => {
        setMulti(prev => {
            const next = !prev;
            if (next) handleResetMultiSelect();
            return next;
        });
    }, [handleResetMultiSelect]);

    const handleError = useCallback((err) => {
        console.error("Scanner Error:", err); // Keep console for scanner hardware/library errors
        errorMessage(`Chyba skenera: ${err?.message || err}`, enqueueSnackbar, closeSnackbar);
        errorSound();
    }, [enqueueSnackbar, closeSnackbar]); // Stable deps

    useEffect(() => { persistActualUrl(); }, []); // Run on mount

    return (
        <div className={classes.wrapper}>
            {loading && <Loading />}

            {/* Pass the STABLE wrapper to the scanner */}
            <BarcodeScannerComponent
                onError={handleError}
                onScan={stableScanHandlerWrapper}
            />

            <div className={classes.code}>
                načítaný kód: {code || 'žiadny'}
            </div>

            <Grid component="label" container alignItems="center" justify="center" spacing={1} style={{ marginBottom: 15 }}>
                <Grid item>Single mod</Grid>
                <Grid item>
                    <Switch checked={isMulti} onChange={handleSwitchMulti} color="primary" />
                </Grid>
                <Grid item>Multi mod</Grid>
            </Grid>

            {isMulti && (
                <div className={classes.multiSection}>
                    <Paper component="ul" className={classes.root} elevation={3}>
                        {multiselectShipments.length === 0 ? (
                            <li style={{ padding: '8px 16px', fontStyle: 'italic', color: '#777' }}>
                                Zatiaľ žiadne kódy v multi výbere.
                            </li>
                        ) : (
                            multiselectShipments.map((shipmentCode, key) => (
                                <li key={`${shipmentCode}-${key}`}>
                                    <Chip
                                        label={shipmentCode}
                                        onDelete={handleDeleteChip(shipmentCode)}
                                        className={classes.chip}
                                        color="secondary"
                                    />
                                </li>
                            ))
                        )}
                    </Paper>
                    <div className={classes.spaceForMultiPanel}></div>
                    <MultiSelectPanel
                        selectedShipments={multiselectShipments}
                        removeFromMultiselect={handleDeleteFromMultiselect}
                        allowAssign={true}
                    />
                </div>
            )}

            <Link to="/">
                <Fab color="primary" style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 15px)', right: '15px', zIndex: 1100 }} aria-label="close-scanner">
                    <CloseIcon />
                </Fab>
            </Link>
        </div>
    );
};

export default Scanner;